import React, { useState, useEffect } from 'react';
import {
  ChevronUp,
  ChevronDown,
  Edit2,
  Trash2,
  Star,
  Shield
} from 'lucide-react';
import type { Drill, Coach } from '../../types';
import { formatMinutesSincePracticeStart } from '../../utils/dateTime';
import { supabase } from '../../lib/supabase';

// Import sport-specific icons
import FootballIcon from '../../assets/icons/football.svg';
import BasketballIcon from '../../assets/icons/basketball.svg';
import SoccerIcon from '../../assets/icons/soccer.svg';
import BaseballIcon from '../../assets/icons/baseball.svg';
import SoftballIcon from '../../assets/icons/softball.svg';
import VolleyballIcon from '../../assets/icons/volleyball.svg';
import HockeyIcon from '../../assets/icons/hockey.svg';
import LacrosseIcon from '../../assets/icons/lacrosse.svg';

interface DrillCardProps {
  drill: Drill;
  index: number;
  isCurrent?: boolean;
  isWarning?: boolean;
  startMinutes: number;
  endMinutes: number;
  startTime: string;
  timeFormat: '12h' | '24h';
  coaches: Coach[];
  drillCoaches: Record<string, string[]>;
  favoriteStepIds: string[];
  onMoveDrillUp: (index: number) => void;
  onMoveDrillDown: (index: number) => void;
  onEditDrill: (drill: Drill) => Promise<void>;
  onDeleteDrill: (drillId: string, drillName?: string) => void;
  onDeletePositionGroup: (drillId: string, groupName: string) => Promise<void>;
  onToggleFavorite: (stepId: string) => void;
  onCoachChange: (drillId: string, groupName: string, coachId: string, action: 'add' | 'remove' | 'clear') => void;
  totalDrills: number;
  currentTeam?: { sport?: string };
}

export function DrillCard({
  drill,
  index,
  isCurrent = false,
  isWarning = false,
  startMinutes,
  endMinutes,
  startTime,
  timeFormat,
  coaches,
  drillCoaches,
  favoriteStepIds,
  onMoveDrillUp,
  onMoveDrillDown,
  onEditDrill,
  onDeleteDrill,
  onDeletePositionGroup,
  onToggleFavorite,
  onCoachChange,
  totalDrills,
  currentTeam
}: DrillCardProps) {
  const isWaterBreak = drill.name.toLowerCase().includes('water break');
  const isBreak = drill.name.toLowerCase().includes('break');
  const isTeamMeeting = drill.name.toLowerCase().includes('team meeting');
  const isStretch = drill.name.toLowerCase().includes('stretch');
  const isWarmup = drill.name.toLowerCase().includes('warm up') || drill.name.toLowerCase().includes('warmup');
  const isCooldown = drill.name.toLowerCase().includes('cool down') || drill.name.toLowerCase().includes('cooldown');

  // Debug log - REMOVED to prevent excessive logging
  // console.log(`[DrillCard] Rendering drill: "${drill.name}" with ${drill.position_groups?.length || 0} position groups`);

  // Choose border color based on current and warning states
  let borderColor = '';
  let bgColor = '';
  if (isCurrent) {
    borderColor = 'border-4 border-blue-500 shadow-lg';
    bgColor = 'bg-blue-50';
  } else if (isWarning) {
    borderColor = 'border-4 border-yellow-400';
    bgColor = 'bg-yellow-50';
  } else {
    borderColor = 'border border-gray-200';
    bgColor = isWaterBreak ? 'bg-blue-50' :
             isBreak ? 'bg-gray-50' :
             isTeamMeeting ? 'bg-purple-50' :
             isStretch ? 'bg-green-50' :
             isWarmup ? 'bg-yellow-50' :
             isCooldown ? 'bg-indigo-50' :
             'bg-white';
  }

  // State to track progress
  const [progress, setProgress] = useState(0.5);
  
  // State for lazy-loaded coaching points
  const [coachingPoints, setCoachingPoints] = useState<Record<string, string[]>>({});
  const [loadingSteps, setLoadingSteps] = useState<Set<string>>(new Set());
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const [failedSteps, setFailedSteps] = useState<Set<string>>(new Set());

  // Function to fetch coaching points for a step
  const fetchCoachingPointsForStep = async (stepId: string, isRetry: boolean = false) => {
    if (!stepId || (!isRetry && (coachingPoints[stepId] || loadingSteps.has(stepId)))) {
      return; // Already loaded or loading
    }

    setLoadingSteps(prev => new Set(prev).add(stepId));
    setFailedSteps(prev => {
      const newSet = new Set(prev);
      newSet.delete(stepId); // Remove from failed set when retrying
      return newSet;
    });

    try {
      // console.log(`[DrillCard] Fetching coaching points for step: ${stepId}`);
      
      // Add timeout and retry logic for production RLS issues
      const fetchWithTimeout = async (timeoutMs: number = 5000) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
        
        try {
          const { data, error } = await supabase
            .from('drill_coaching_points')
            .select('point, order_index')
            .eq('drill_step_id', stepId)
            .order('order_index')
            .abortSignal(controller.signal);
            
          clearTimeout(timeoutId);
          return { data, error };
        } catch (error: any) {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            throw new Error('Request timeout');
          }
          throw error;
        }
      };

      // Try with a shorter timeout first, then retry with longer timeout
      let data, error;
      try {
        // console.log(`[DrillCard] Attempting coaching points fetch with 5s timeout...`);
        ({ data, error } = await fetchWithTimeout(5000));
      } catch (timeoutError) {
        // console.log(`[DrillCard] First attempt timed out, retrying with 10s timeout...`);
        try {
          ({ data, error } = await fetchWithTimeout(10000));
        } catch (secondTimeoutError) {
          // console.log(`[DrillCard] Second attempt failed, skipping coaching points for step ${stepId}`);
          // Set empty coaching points to prevent further attempts
          setCoachingPoints(prev => ({
            ...prev,
            [stepId]: []
          }));
          setFailedSteps(prev => new Set(prev).add(stepId));
          return;
        }
      }

      if (error) {
        console.error('Error fetching coaching points:', error);
        // Set empty array to prevent further attempts
        setCoachingPoints(prev => ({
          ...prev,
          [stepId]: []
        }));
        setFailedSteps(prev => new Set(prev).add(stepId));
        return;
      }

      const points = data?.map(cp => cp.point) || [];
      console.log(`[DrillCard] Successfully loaded ${points.length} coaching points for step ${stepId}`);
      
      setCoachingPoints(prev => ({
        ...prev,
        [stepId]: points
      }));
    } catch (error: any) {
      console.error('Error fetching coaching points:', error);
      // Set empty array to prevent further attempts and show graceful fallback
      setCoachingPoints(prev => ({
        ...prev,
        [stepId]: []
      }));
      setFailedSteps(prev => new Set(prev).add(stepId));
    } finally {
      setLoadingSteps(prev => {
        const newSet = new Set(prev);
        newSet.delete(stepId);
        return newSet;
      });
    }
  };

  // Helper to get sport icon
  const getSportIcon = (sport: string | undefined) => {
    switch (sport) {
      case 'Football':
        return <img src={FootballIcon} alt="Football" className="h-8 w-8" />;
      case 'FlagFootball':
        return <img src={FootballIcon} alt="Flag Football" className="h-8 w-8" />;
      case 'Basketball':
        return <img src={BasketballIcon} alt="Basketball" className="h-8 w-8" />;
      case 'Soccer':
        return <img src={SoccerIcon} alt="Soccer" className="h-8 w-8" />;
      case 'Baseball':
        return <img src={BaseballIcon} alt="Baseball" className="h-8 w-8" />;
      case 'Softball':
        return <img src={SoftballIcon} alt="Softball" className="h-8 w-8" />;
      case 'Volleyball':
        return <img src={VolleyballIcon} alt="Volleyball" className="h-8 w-8" />;
      case 'Hockey':
        return <img src={HockeyIcon} alt="Hockey" className="h-8 w-8" />;
      case 'Lacrosse':
        return <img src={LacrosseIcon} alt="Lacrosse" className="h-8 w-8" />;
      default:
        return <Shield className="h-8 w-8 text-blue-500" />;
    }
  };
  
  // Update progress periodically if this is the current drill
  useEffect(() => {
    if (!isCurrent) return;
    
    // Calculate current progress
    const calculateProgress = () => {
      try {
        // Calculate elapsed time based on current time
        const now = new Date();
        const [practiceStartHours, practiceStartMinutes] = startTime.split(':').map(Number);
        const practiceStartDate = new Date();
        practiceStartDate.setHours(practiceStartHours, practiceStartMinutes, 0, 0);
    
        // Calculate minutes since practice start
        const minutesSincePracticeStart = Math.floor((now.getTime() - practiceStartDate.getTime()) / 60000);
        
        // Calculate drill elapsed time
        const drillElapsed = minutesSincePracticeStart - startMinutes;
        const total = endMinutes - startMinutes;
        
        // Ensure progress is between 0 and 1
        return Math.max(0, Math.min(1, total > 0 ? drillElapsed / total : 0));
      } catch (error) {
        console.error('Error calculating drill progress:', error);
        return 0.5;
      }
    };
    
    // Set initial progress
    setProgress(calculateProgress());
    
    // Update progress every 30 seconds instead of 15 to reduce re-renders
    const interval = setInterval(() => {
      setProgress(calculateProgress());
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isCurrent, startTime, startMinutes, endMinutes]);
  
  // Calculate icon position based on progress
  const progressTop = isCurrent ? `calc(${progress * 100}% - 16px)` : '50%';

  // Get sport icon
  const sportIcon = isCurrent && (
    <div
      className="absolute left-[-20px] z-10 bg-white rounded-full p-1 shadow-lg"
      style={{ top: progressTop, transform: 'translateY(-50%)' }}
    >
      {/* Use the getSportIcon function here */}
      {getSportIcon(currentTeam?.sport)}
    </div>
  );

  // Create the timeline line element
  const timelineLine = isCurrent && (
    <div
      className="absolute left-0 right-0 h-1 bg-blue-200 z-0"
      style={{ top: progressTop, pointerEvents: 'none' }}
    />
  );

  // Helper to safely get coaching points text
  const getCoachingPointText = (point: any): string => {
    if (!point) return '';
    if (typeof point === 'string') return point;
    if (typeof point === 'object' && point.point) return point.point;
    return String(point);
  };

  return (
    <div
      className={`relative p-6 mb-6 rounded-lg ${bgColor} ${borderColor}`}
      style={{ overflow: 'visible' }}
    >
      {sportIcon}
      {timelineLine}
      {isCurrent && (
        <div className="absolute left-1/2 -translate-x-1/2 top-0 z-20 bg-blue-600 text-white text-xs px-3 py-1 rounded-b shadow text-center">
          CURRENT DRILL
        </div>
      )}
      <div className="w-full">
        <div className="flex justify-between items-start mb-2">
          <div className="text-sm font-medium text-gray-500 mt-1">
            {formatMinutesSincePracticeStart(startMinutes, startTime, timeFormat)} - {formatMinutesSincePracticeStart(endMinutes, startTime, timeFormat)} <br />
            ({endMinutes - startMinutes} min)
          </div>
          <div className="flex items-center gap-0">
            <button
              onClick={() => onMoveDrillUp(index)}
              className="p-0.5 hover:bg-gray-100 rounded"
              disabled={index === 0}
              title="Move drill up"
            >
              <ChevronUp size={16} className={index === 0 ? 'text-gray-300' : 'text-gray-600'} />
            </button>
            <button
              onClick={() => onMoveDrillDown(index)}
              className="p-0.5 hover:bg-gray-100 rounded"
              disabled={index === totalDrills - 1}
              title="Move drill down"
            >
              <ChevronDown size={16} className={index === totalDrills - 1 ? 'text-gray-300' : 'text-gray-600'} />
            </button>
            <button
              onClick={() => onEditDrill(drill)}
              className="p-0.5 hover:bg-gray-100 rounded"
              title="Edit drill"
            >
              <Edit2 size={16} className="text-gray-600" />
            </button>
            <button
              onClick={() => onDeleteDrill(drill.id, drill.name)}
              className="p-0.5 hover:bg-gray-100 rounded"
              title="Delete drill"
            >
              <Trash2 size={16} className="text-red-600" />
            </button>
          </div>
        </div>
        <div className="mb-2">
          <h4 className="font-medium text-lg">{drill.name}</h4>
          {drill.intensity_level && (
            <div className="mt-1">
              <span className={`badge ${
                drill.intensity_level === 'Game speed' 
                  ? 'badge-danger' 
                  : drill.intensity_level === 'Technique focused'
                  ? 'badge-primary'
                  : 'badge-success'
              }`}>
                {drill.intensity_level}
              </span>
            </div>
          )}
        </div>
        <div className="text-sm text-gray-600 mb-2">{drill.focus_areas?.join(', ')}</div>
        
        <div className="mt-4">
          {drill.position_groups?.map((group, groupIndex) => {
            const groupName = typeof group === 'string' ? group : group.group_name;
            const groupSteps = typeof group === 'string' ? [] : group.steps;
            
            // Skip rendering empty groups except for ALL
            if (groupName !== 'ALL' && (!groupSteps || groupSteps.length === 0)) {
              return null;
            }
            
            return (
              <div key={`group-${index}-${groupIndex}`} className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium">{groupName}</h4>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeletePositionGroup(drill.id, groupName);
                      }}
                      className="p-1 hover:bg-red-100 rounded text-red-600 hover:text-red-700"
                      title={`Remove ${groupName} position group`}
                    >
                      <Trash2 size={16} />
                    </button>
                    <select
                      className="px-2 py-1 border rounded-md text-sm bg-white min-w-32"
                      value=""
                      onChange={(e) => {
                        if (e.target.value && e.target.value !== 'ALL') {
                          onCoachChange(drill.id, groupName, e.target.value, 'add');
                          e.target.value = '';
                        }
                      }}
                    >
                      <option value="">+ Add Coach</option>
                      {coaches
                        .filter(coach => !drillCoaches[`${drill.id}-${groupName}`]?.includes(coach.id))
                        .map(coach => (
                          <option key={coach.id} value={coach.id}>
                            {coach.name}
                          </option>
                        ))}
                    </select>
                    {(drillCoaches[`${drill.id}-${groupName}`]?.length || 0) > 0 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onCoachChange(drill.id, groupName, '', 'clear');
                        }}
                        className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                        title="Clear all coaches"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Coach assignments displayed below the position group label */}
                {drillCoaches[`${drill.id}-${groupName}`]?.length > 0 && (
                  <div className="mb-3">
                    <div className="flex flex-wrap gap-0.5">
                      {drillCoaches[`${drill.id}-${groupName}`].map((coachId: string) => {
                        const coach = coaches.find(c => c.id === coachId);
                        return coach ? (
                          <span
                            key={coachId}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 bg-green-100 text-green-800 text-xs rounded-full border border-green-200 leading-tight h-5"
                          >
                            {coach.name}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onCoachChange(drill.id, groupName, coachId, 'remove');
                              }}
                              className="text-green-600 hover:text-green-800 ml-0.5 hover:bg-green-200 rounded-full w-3 h-3 flex items-center justify-center text-xs"
                              title="Remove coach"
                            >
                              ×
                            </button>
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {groupSteps && groupSteps.map((step, stepIndex) => {
                    // Create a fallback ID in case the step.id is undefined
                    const safeStepId = step.id || `temp-${drill.id}-${groupName}-${stepIndex}`;
                    
                    return (
                      <div key={`step-${index}-${groupIndex}-${stepIndex}`} className="space-y-2">
                        <button
                          className="w-full font-medium cursor-pointer hover:bg-primary hover:bg-opacity-10 flex items-center justify-between gap-2 rounded-lg px-4 py-2 border shadow-sm bg-primary bg-opacity-10 border-primary border-opacity-20"
                          onClick={async () => {
                            const stepKey = `${index}-${groupIndex}-${stepIndex}`;
                            const stepElement = document.getElementById(`step-content-${stepKey}`);
                            const arrowElement = document.getElementById(`step-arrow-${stepKey}`);
                            
                            if (stepElement && arrowElement) {
                              const isExpanding = stepElement.classList.contains('hidden');
                              
                              console.log(`[DrillCard] ${isExpanding ? 'Expanding' : 'Collapsing'} step: "${step.name}" (ID: ${step.id})`);
                              
                              stepElement.classList.toggle('hidden');
                              arrowElement.classList.toggle('rotate-180');
                              
                              // Track expanded state
                              setExpandedSteps(prev => {
                                const newSet = new Set(prev);
                                if (isExpanding) {
                                  newSet.add(stepKey);
                                } else {
                                  newSet.delete(stepKey);
                                }
                                return newSet;
                              });
                              
                              // If expanding and step has an ID, fetch coaching points
                              if (isExpanding && step.id) {
                                await fetchCoachingPointsForStep(step.id);
                              }
                            }
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                onToggleFavorite(safeStepId);
                              }}
                              className="p-1 hover:bg-gray-100 rounded cursor-pointer"
                              title={favoriteStepIds.includes(safeStepId) ? "Remove from favorites" : "Add to favorites"}
                            >
                              <Star
                                size={16}
                                className={favoriteStepIds.includes(safeStepId) ? "text-yellow-500 fill-yellow-500" : "text-gray-400"}
                              />
                            </div>
                            <span className="text-sm truncate max-w-[200px]" title={step.name}>{step.name}</span>
                          </div>
                          <svg
                            className="w-4 h-4 transform transition-transform"
                            id={`step-arrow-${index}-${groupIndex}-${stepIndex}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                        <div id={`step-content-${index}-${groupIndex}-${stepIndex}`} className="hidden bg-white rounded-b-lg border border-t-0 border-gray-200 px-4 py-3 space-y-2">
                          {step.setup && (
                            <div className="text-gray-600">
                              <span className="font-medium">Setup:</span> {step.setup}
                            </div>
                          )}
                          {step.execution && (
                            <div className="text-gray-600">
                              <span className="font-medium">Execution:</span> {step.execution}
                            </div>
                          )}
                          {step.equipment && (
                            <div className="text-gray-600">
                              <span className="font-medium">Equipment:</span> {step.equipment}
                            </div>
                          )}
                          {step.video_url && (
                            <div className="text-gray-600">
                              <span className="font-medium">Video:</span>{" "}
                              <a 
                                href={step.video_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 hover:underline"
                              >
                                View Videos
                              </a>
                            </div>
                          )}
                          {/* Lazy-loaded coaching points */}
                          {step.id && (
                            <div className="text-gray-600">
                              <span className="font-medium">Coaching Points:</span>
                              {step.id && loadingSteps.has(step.id) ? (
                                <div className="text-gray-500 italic ml-2">Loading coaching points...</div>
                              ) : step.id && failedSteps.has(step.id) ? (
                                <div className="ml-2 flex items-center gap-2">
                                  <span className="text-gray-400 text-xs italic">Failed to load coaching points</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (step.id) {
                                        fetchCoachingPointsForStep(step.id, true);
                                      }
                                    }}
                                    className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                  >
                                    Retry
                                  </button>
                                </div>
                              ) : (
                                (() => {
                                  const lazyCoachingPoints = step.id ? (coachingPoints[step.id] || []) : [];
                                  const existingCoachingPoints = Array.isArray(step.coaching_points) ? step.coaching_points : [];
                                  
                                  // Use lazy-loaded points if available, otherwise fall back to existing points
                                  const pointsToShow = lazyCoachingPoints.length > 0 ? lazyCoachingPoints : existingCoachingPoints;
                                  
                                  if (pointsToShow.length > 0) {
                                    return (
                                      <ul className="list-disc list-inside ml-2">
                                        {pointsToShow.map((point: string, pointIndex: number) => (
                                          <li key={`point-${index}-${groupIndex}-${stepIndex}-${pointIndex}`}>
                                            {getCoachingPointText(point)}
                                          </li>
                                        ))}
                                      </ul>
                                    );
                                  } else {
                                    // Check if we've attempted to load but got empty results (vs. never loaded)
                                    const hasAttemptedLoad = step.id ? coachingPoints.hasOwnProperty(step.id) : false;
                                    if (hasAttemptedLoad) {
                                      return <div className="text-gray-400 text-xs italic ml-2">No coaching points available</div>;
                                    } else {
                                      return <div className="text-gray-500 text-xs italic ml-2">Click to expand and load coaching points</div>;
                                    }
                                  }
                                })()
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DrillCard; 